import { formatNumber } from "@wrstudios/utils";
import { debounce, groupBy, merge } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 } from "uuid";
import * as listingApi from "../../../api/listing";
import * as api from "../../../api/report";
import {
  getListingAdjustmentUpdates,
  transformListing,
  transformSchools,
  transformZestimate
} from "../../../utils/listing";

function useListings({
  reportId,
  subModules,
  updateSubModules,
  addSubModule,
  toggleSubModule
}) {
  const [zestimateRequest, setZestimateRequest] = useState({
    loading: true,
    success: false,
    error: null
  });
  const [isAddingListing, setIsAddingListing] = useState(false);
  const [forgeListings, setForgeListings] = useState([]);
  const [ghostListings, setGhostListings] = useState([]);
  const [myRecentSales, setMyRecentSales] = useState([]);
  const listings = subModules.map((subModule) => subModule.data);
  const listingsWithForgeListings = [...listings, ...forgeListings];
  const visibleListings = subModules
    .map((subModule) => !subModule.isHidden && subModule.data)
    .filter(Boolean);
  const visibleListingsWithGhostListings = subModules
    .map((subModule) => {
      const listing = subModule.data;
      const isGhostListing = ghostListings.some(
        (l) => l.recordId === listing.recordId
      );
      return isGhostListing
        ? attachGhost(listing)
        : !subModule.isHidden && listing;
    })
    .filter(Boolean);

  const visibleListingGroups = groupListings([
    ...ghostListings,
    ...visibleListings
  ]);

  const addListingAdjustment = async ({ listingRecordId, name, value }) => {
    const fakeAdjustment = { id: v4(), name, value: Number(value) };
    updateSubModules("comps", (subModule) => {
      const listing = subModule.data;
      if (listing.recordId === listingRecordId) {
        return {
          ...subModule,
          data: {
            ...listing,
            ...getListingAdjustmentUpdates({
              price: listing.price,
              sqft: listing.sqft,
              acres: listing.acres,
              adjustments: [...listing.adjustments, fakeAdjustment]
            })
          }
        };
      }
      return subModule;
    });
    const realAdjustment = await listingApi.addAdjustment(listingRecordId, {
      name,
      value: formatNumber(value, "0[.]0")
    });
    updateSubModules("comps", (subModule) => {
      const listing = subModule.data;
      if (listing.recordId === listingRecordId) {
        const adjustments = listing.adjustments.map((adjustment) => {
          if (adjustment.id === fakeAdjustment.id) {
            adjustment.id = realAdjustment.id;
          }
          return adjustment;
        });
        return {
          ...subModule,
          data: {
            ...listing,
            ...getListingAdjustmentUpdates({
              price: listing.price,
              sqft: listing.sqft,
              acres: listing.acres,
              adjustments
            })
          }
        };
      }
      return subModule;
    });
  };

  const updateListingAdjustment = useRef(
    debounce(({ listingRecordId, adjustmentId, name, value }) => {
      listingApi.updateAdjustment(listingRecordId, {
        id: adjustmentId,
        name,
        value: formatNumber(value, "0[.]0")
      });
      updateSubModules("comps", (subModule) => {
        const listing = subModule.data;
        if (listing.recordId === listingRecordId) {
          const adjustments = listing.adjustments.map((adjustment) => {
            if (adjustment.id === adjustmentId) {
              adjustment.name = name;
              adjustment.value = Number(value);
            }
            return adjustment;
          });
          return {
            ...subModule,
            data: {
              ...listing,
              ...getListingAdjustmentUpdates({
                price: listing.price,
                sqft: listing.sqft,
                acres: listing.acres,
                adjustments
              })
            }
          };
        }
        return subModule;
      });
    }, 200)
  ).current;

  const removeListingAdjustment = ({ listingRecordId, adjustmentId }) => {
    updateSubModules("comps", (subModule) => {
      const listing = subModule.data;
      if (listing.recordId === listingRecordId) {
        return {
          ...subModule,
          data: {
            ...listing,
            ...getListingAdjustmentUpdates({
              price: listing.price,
              sqft: listing.sqft,
              acres: listing.acres,
              adjustments: listing.adjustments.filter(
                (adjustment) => adjustment.id !== adjustmentId
              )
            })
          }
        };
      }
      return subModule;
    });
    listingApi.removeAdjustment(listingRecordId, adjustmentId);
  };

  const toggleListing = async (listingRecordId) => {
    if (isAddingListing) return;
    const listing = listings.find(
      (listing) => listing.recordId === listingRecordId
    );
    const forgeListing = forgeListings.find(
      (listing) => listing.recordId === listingRecordId
    );
    const isForgeListing = !!forgeListing && !listing;

    if (isForgeListing) {
      setIsAddingListing(true);
      const newListing = await api.addListing(reportId, {
        mlsnum: forgeListing.mlsNumber
      });
      const newListingWithForgeData = merge(
        {},
        forgeListing,
        transformListing(newListing)
      );
      addSubModule("comps", newListingWithForgeData);
      setIsAddingListing(false);
    } else {
      // We are unhiding a listing
      if (listing.isHidden) {
        setGhostListings((ghostListings) =>
          ghostListings.filter((l) => l.recordId !== listing.recordId)
        );
      }
      // We are hiding a listing
      else {
        setGhostListings((ghostListings) => [
          ...ghostListings,
          attachGhost(listing)
        ]);
      }
      toggleSubModule("comps", `listing/${listing.recordId}`);
    }
  };

  const clearGhostListings = () => {
    setGhostListings([]);
  };

  const getZestimates = useCallback(() => {
    setZestimateRequest({ loading: true, success: false, error: null });
    api
      .getZestimates(reportId)
      .then((data) => {
        updateSubModules("comps", (subModule) => {
          const listing = subModule.data;
          const zestimate = data[listing.recordId] || {};

          return {
            ...subModule,
            data: {
              ...listing,
              zestimate: transformZestimate({
                ...listing,
                zestimate: [zestimate.amount || 0, zestimate.url || ""]
              })
            }
          };
        });
        setZestimateRequest({ loading: false, success: true, error: null });
      })
      .catch((error) => {
        console.error(error);
        setZestimateRequest({ loading: false, success: false, error });
      });
  }, [reportId]);

  const getSchools = useCallback(
    (listing) => {
      return api
        .getSchools(reportId, {
          lat: listing.coords.lat,
          lon: listing.coords.lon
        })
        .then(({ schools }) => {
          if (listing.isForgeListing) {
            setForgeListings((forgeListings) =>
              forgeListings.map((forgeListing) => {
                if (forgeListing.recordId === listing.recordId) {
                  return {
                    ...forgeListing,
                    schools: transformSchools(schools)
                  };
                }
                return forgeListing;
              })
            );
          } else if (listing.isMyRecentSalesListing) {
            setMyRecentSales((myRecentSales) =>
              myRecentSales.map((myRecentSale) => {
                if (myRecentSale.recordId === listing.recordId) {
                  return {
                    ...myRecentSale,
                    schools: transformSchools(schools)
                  };
                }
                return myRecentSale;
              })
            );
          } else {
            updateSubModules("comps", (subModule) => {
              if (subModule.data.recordId === listing.recordId) {
                return {
                  ...subModule,
                  data: {
                    ...subModule.data,
                    schools: transformSchools(schools)
                  }
                };
              }
              return subModule;
            });
          }
        });
    },
    [reportId]
  );

  // Load zestimates when we load the report
  useEffect(() => {
    if (reportId) {
      getZestimates();
    }
  }, [reportId, getZestimates]);

  return {
    visibleListings,
    visibleListingsWithGhostListings,
    visibleListingGroups,
    listingsWithForgeListings,
    forgeListings,
    setForgeListings,
    toggleListing,
    isAddingListing,
    ghostListings,
    clearGhostListings,
    zestimateRequest,
    getSchools,
    addListingAdjustment,
    updateListingAdjustment,
    removeListingAdjustment,
    myRecentSales,
    setMyRecentSales
  };
}

function groupListings(listings) {
  const modifiedListings = listings.map((listing) => {
    if (!listing.status) {
      return { ...listing, status: "No Status" };
    }
    return listing;
  });
  const statusMap = ["S", "P", "B", "A", "X", "W", "C", "H"];
  const listingsWithStatusInStatusMap = statusMap.reduce((groups, letter) => {
    const group = modifiedListings.filter(
      (listing) => listing.statusAsLetter === letter
    );
    return [...groups, ...group];
  }, []);
  const listingsWithoutStatusInStatusMap = modifiedListings.filter(
    (listing) => !statusMap.includes(listing.statusAsLetter)
  );
  return {
    ...groupBy(listingsWithStatusInStatusMap, "status"),
    ...groupBy(listingsWithoutStatusInStatusMap, "status")
  };
}

function attachGhost(listing) {
  return { ...listing, isGhost: true };
}

export default useListings;
